import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Heart } from "lucide-react";
import { inr, resolveImg } from "../lib/api";
import { useWishlist } from "../context/WishlistContext";
import Stars from "./Stars";

export default function ProductCard({ product, index = 0 }) {
  const { has, toggle } = useWishlist();
  const saved = has(product.id);
  const discount =
    product.mrp > product.price
      ? Math.round(((product.mrp - product.price) / product.mrp) * 100)
      : 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: (index % 4) * 0.08 }}
      data-testid={`product-card-${product.id}`}
    >
      <Link to={`/product/${product.id}`} className="luxury-card group block overflow-hidden">
        <div className="relative overflow-hidden aspect-[4/5] rounded-t-[20px]">
          <img
            src={resolveImg(product.images?.[0])}
            alt={product.name}
            className="w-full h-full object-cover"
            loading="lazy"
          />
          {discount > 0 && product.in_stock !== false && (
            <span className="absolute top-3 left-3 bg-accent text-accent-foreground text-[11px] font-semibold px-2.5 py-1 rounded-md tracking-wide">
              {discount}% OFF
            </span>
          )}
          {product.in_stock === false && (
            <span
              className="absolute top-3 left-3 bg-foreground/80 text-background text-[11px] font-semibold px-2.5 py-1 rounded-md tracking-wide"
              data-testid={`out-of-stock-${product.id}`}
            >
              OUT OF STOCK
            </span>
          )}
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              toggle(product.id);
            }}
            aria-label={saved ? "Remove from wishlist" : "Add to wishlist"}
            className="absolute top-3 right-3 h-9 w-9 rounded-full bg-background/85 backdrop-blur flex items-center justify-center shadow-sm hover:scale-110 transition-transform"
            data-testid={`wishlist-toggle-${product.id}`}
          >
            <Heart
              size={17}
              strokeWidth={1.8}
              className={saved ? "text-accent fill-accent" : "text-foreground/60"}
            />
          </button>
        </div>
        <div className="px-4 pt-4 pb-5">
          <p className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
            {product.category}
          </p>
          <h3 className="mt-1.5 font-display text-lg leading-snug text-foreground group-hover:text-primary transition-colors">
            {product.name}
          </h3>
          {product.rating_count > 0 && (
            <div className="mt-1.5 flex items-center gap-1.5">
              <Stars value={product.rating_avg} size={13} />
              <span className="text-xs text-muted-foreground">({product.rating_count})</span>
            </div>
          )}
          <div className="mt-2 flex items-baseline gap-2">
            <span className="text-base font-semibold text-primary">{inr(product.price)}</span>
            {discount > 0 && (
              <span className="text-sm text-muted-foreground line-through">{inr(product.mrp)}</span>
            )}
          </div>
        </div>
      </Link>
    </motion.div>
  );
}
